import { useEffect, useState } from 'react';
import { format } from 'date-fns';

import { Box, DateInput, DatePicker, Label, TextButton } from '@zillow/constellation-10';

import { AppSelectors } from '../../selectors';
import { useSelector } from '../../selectors/useSelector';

import { getFormattedDateForDatePicker } from '../../utils/dateUtils';

import type { ChangeEvent, KeyboardEvent } from 'react';

type Props = {
  initialDate?: string;
  label?: string;
  inputName?: string;
  onDateChange: (newValue: string) => void;
  onInputBlur?: (event: ChangeEvent<HTMLInputElement>) => void;
  showNextMonthButton?: boolean;
  setDefaultDate?: boolean;
  minDate?: string;
  setMaxDateOneYearAhead?: boolean;
  defaultPastDateToToday?: boolean;
  saveOnDateSelect?: boolean;
};

const DatePickerWithDateSelect = ({
  initialDate = '',
  label = 'Date available',
  onDateChange,
  onInputBlur,
  inputName,
  showNextMonthButton = true,
  setDefaultDate = true,
  minDate,
  setMaxDateOneYearAhead = false,
  defaultPastDateToToday = true,
  saveOnDateSelect = true,
}: Props) => {
  const isScreenDesktop = useSelector((state) => AppSelectors.makeIsScreenDesktop(state));

  const [pickedDate, setPickedDate] = useState('');
  const [firstOfNextMonth, setFirstOfNextMonth] = useState('');

  const today = new Date();
  const startOfYesterday = new Date(today.getTime() - 24 * 3600 * 1000);

  const firstDate = minDate ? new Date(minDate) : startOfYesterday;
  const lastDate = (() => {
    const date = new Date(firstDate.getTime() + 24 * 3600 * 1000);
    date.setFullYear(firstDate.getFullYear() + 1);
    return date;
  })();

  useEffect(() => {
    const startOfNextMonth = format(new Date(today.getFullYear(), today.getMonth() + 1), 'MM/dd/yyyy');
    const formattedDate = getFormattedDateForDatePicker({ initialDate, today, defaultPastDateToToday });

    if (initialDate || setDefaultDate) {
      setPickedDate(formattedDate);
      onDateChange(formattedDate);
    }
    setFirstOfNextMonth(startOfNextMonth);
  }, [initialDate]);

  const handleChange = (value: string | Date) => {
    const formatted = typeof value === 'string' ? value : format(value, 'MM/dd/yyyy');
    setPickedDate(formatted);
    onDateChange(formatted);
  };

  const handleDatePickerClose = () => {
    setTimeout(() => {
      if (saveOnDateSelect && pickedDate && onInputBlur) {
        onInputBlur({
          target: { name: inputName ?? '', value: pickedDate },
        } as React.ChangeEvent<HTMLInputElement>);
      }
    }, 0);
  };

  const handleSetMonthKeyUp = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && event.target instanceof HTMLButtonElement) {
      event.target.click();
    }
  };

  const handleSetMonthOnClick = () => {
    handleChange(new Date(firstOfNextMonth));
    // @ts-expect-error: We only need target.name and target.value for onInputBlur, not a full event object
    onInputBlur({ target: { name: inputName, value: firstOfNextMonth } });
  };

  const renderInput = () => {
    return (
      <DatePicker
        value={pickedDate}
        onValueChange={(newValue: string | Date) => handleChange(newValue)}
        onOpenChange={handleDatePickerClose}
        minDate={firstDate}
        maxDate={setMaxDateOneYearAhead ? lastDate : undefined}
        size={isScreenDesktop ? 'lg' : 'md'}
        overflowPadding={{ bottom: 32 }}
        input={
          <DateInput
            onBlur={() =>
              onInputBlur?.({
                target: { name: inputName ?? '', value: pickedDate },
              } as React.ChangeEvent<HTMLInputElement>)
            }
            data-testid="date-picker"
            placeholder="MM/DD/YYYY"
          />
        }
      />
    );
  };

  return (
    <div data-testid="DatePickerContainer">
      <Label>{label}</Label>
      <Box css={{ marginBottom: '100' }} />

      {renderInput()}

      {showNextMonthButton && (
        <TextButton
          tone="brand"
          onClick={handleSetMonthOnClick}
          onKeyUp={handleSetMonthKeyUp}
          type="button"
          css={{ marginTop: '600' }}
        >
          {`Set to ${firstOfNextMonth}`}
        </TextButton>
      )}
    </div>
  );
};

export default DatePickerWithDateSelect;
